import { useAuth } from "../App";
import { Link, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Bot, 
  Calculator, 
  FileText, 
  LogOut,
  Menu,
  X,
  AlertTriangle
} from "lucide-react";
import { useState } from "react";
import { Button } from "../components/ui/button";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/agents", label: "AI Agents", icon: Bot },
  { path: "/risk-assessment", label: "Risk Assessment", icon: Calculator },
  { path: "/resources", label: "Resources", icon: FileText },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_50f5a9a4-7203-4eda-9166-5e288543e67f/artifacts/9ya4uhxn_PointeCaptiallargeLogo.png" 
                  alt="Pointe Capital Management" 
                  className="h-10"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path !== "/" && location.pathname.startsWith(item.path));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive 
                        ? "text-[#8B0000] bg-red-50" 
                        : "text-slate-600 hover:text-[#8B0000] hover:bg-slate-50"
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              <span className="hidden sm:block text-sm text-slate-500">{user?.email}</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={logout}
                className="text-slate-600 hover:text-[#8B0000]"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="px-4 py-2 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium ${
                      isActive 
                        ? "text-[#8B0000] bg-red-50" 
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </Link>
                );
              })}
              <div className="pt-2 border-t border-slate-200">
                <p className="px-4 py-2 text-sm text-slate-500">{user?.email}</p>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer Disclaimer */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">AI Disclaimer</p>
              <p>
                This platform uses AI-powered tools for informational purposes only. Do not rely solely on AI-generated 
                analysis for compliance decisions. Always consult with qualified professionals and refer to official 
                regulatory guidance. Contact Chief Compliance Officer Colin Darke at{" "}
                <a href="mailto:colindarke@complisun.com" className="underline font-medium">colindarke@complisun.com</a>{" "}
                or <a href="mailto:pointecapital@complisun.com" className="underline font-medium">pointecapital@complisun.com</a>{" "}
                with any questions.
              </p>
            </div>
          </div>
          <p className="text-center text-xs text-slate-500 mt-4">
            © {new Date().getFullYear()} Pointe Capital Management LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
